import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { RefObject } from "react";
import { useToast } from "@astrum/ui";
import { api, ApiError } from "../../lib/api.js";
import type { Message, MessageFlagKind } from "../../lib/api.js";
import { useAuth } from "../../lib/auth.js";
import { newClientMessageId } from "../../lib/chat-format.js";
import { useReconnected, useRealtimeFrames } from "../../lib/realtime-context.js";
import { isPendingScan, useScanPolling } from "../../lib/scan-status.js";
import { SendQueue } from "../../lib/send-queue.js";
import { MESSAGE_PAGE } from "./types.js";
import type { PendingMessage } from "./types.js";

type MessagePage = {
  messages: Message[];
  hasMore: boolean;
  readPositions?: { userId: string; lastReadMessageId: string | null }[];
};

export interface ChannelMessagesOptions {
  /**
   * The thread's scroll container. The hook keeps it pinned to the newest
   * message and holds the reader's place when an older page is prepended,
   * because only the hook knows which update was which.
   */
  scrollRef: RefObject<HTMLElement | null>;
  /** The open channel has been read up to `messageId` (see markRead in useChannels). */
  onRead: (channelId: string, messageId: string) => void;
  /** A message landed in the open channel — the sidebar's "last activity" moves. */
  onActivity: (channelId: string, createdAt: string) => void;
}

export interface ChannelMessages {
  messages: PendingMessage[];
  /** The first page of the open channel is being fetched. */
  loading: boolean;
  /**
   * The channel whose first page `messages` holds, or null until it lands.
   * `loading` alone cannot say this: on the render that switches channels it
   * is still false and `messages` is still the previous channel's (or empty).
   */
  loadedChannelId: string | null;
  /** Whether an older page exists beyond what is loaded, and whether one is
   *  currently being fetched — both reset on every channel switch. */
  hasMoreOlder: boolean;
  loadingOlder: boolean;
  /** Newest message id everyone else has read past; null if anyone is behind. */
  readWatermark: string | null;
  loadOlder: () => void;
  /** Appends an optimistic row and queues the send. */
  send: (body: string, replyingTo: PendingMessage | null) => void;
  /** Resends a message that failed. */
  retry: (message: PendingMessage) => void;
  toggleReaction: (messageId: string, emoji: string) => void;
  setFlag: (messageId: string, kind: MessageFlagKind | null) => void;
  remind: (messageId: string, at: Date) => void;
  /** Withdraws a message; resolves false (having said why) if the server refused. */
  deleteMessage: (messageId: string) => Promise<boolean>;
  /** Puts the server's copy of a message in place of the one held, e.g. after an edit. */
  replaceMessage: (message: Message) => void;
  togglePin: (messageId: string, pinned: boolean) => void;
  toggleSaved: (messageId: string, saved: boolean) => void;
  /** Author only: removes the link preview for everyone. */
  removePreview: (messageId: string) => void;
  /** Shows the card for a task just raised from a message. */
  attachTask: (messageId: string, task: NonNullable<Message["linkedTask"]>) => void;
  /** Replaces the thread with `channelId`'s newest page. Never rejects. */
  reloadLatest: (channelId: string) => Promise<void>;
}

/**
 * Everything about one channel's messages: the first page and older pages,
 * live frames, the refetch after a reconnect, optimistic sends with retry, and
 * the per-message mutations (reactions, flags, delete, edit, linked task).
 */
export function useChannelMessages(channelId: string | undefined, options: ChannelMessagesOptions): ChannelMessages {
  const { viewer } = useAuth();
  const { toast } = useToast();
  const { scrollRef } = options;

  // The callbacks are read through a ref so a caller can pass inline closures
  // without them becoming dependencies of every effect and callback below.
  const optionsRef = useRef(options);
  useEffect(() => {
    optionsRef.current = options;
  });
  const onRead = useCallback((id: string, messageId: string) => optionsRef.current.onRead(id, messageId), []);
  const onActivity = useCallback((id: string, createdAt: string) => optionsRef.current.onActivity(id, createdAt), []);

  const [messages, setMessages] = useState<PendingMessage[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadedChannelId, setLoadedChannelId] = useState<string | null>(null);
  const [hasMoreOlder, setHasMoreOlder] = useState(false);
  const [loadingOlder, setLoadingOlder] = useState(false);

  /**
   * How far every other member has read: userId -> their last read message id,
   * null for someone who has never opened the channel.
   *
   * Kept per member rather than as one figure because the answer to "has
   * everyone read this" excludes whoever is asking, so it cannot be computed
   * once on the server and broadcast.
   */
  const [readPositions, setReadPositions] = useState<Map<string, string | null>>(new Map());

  /**
   * The newest message id everyone else has read past, or null if anyone is
   * behind. Message ids are UUIDv7 and so sort chronologically, which makes
   * "read" a string comparison rather than a lookup per message.
   *
   * The slowest reader wins: in a channel of five, one person opening it does
   * not mean the message was read. That is honest at the cost of one absent
   * member holding the channel at a single tick.
   */
  const readWatermark = useMemo(() => {
    if (readPositions.size === 0) return null;
    let lowest: string | null = null;
    for (const position of readPositions.values()) {
      if (!position) return null;
      if (lowest === null || position < lowest) lowest = position;
    }
    return lowest;
  }, [readPositions]);

  // No realtime event fires when a scan finishes, so poll the loaded page while
  // an attachment is still "Scanning…" and fold only the new statuses back in
  // (never replacing the list, which may hold optimistic messages).
  useScanPolling(
    messages.some((m) => m.attachments?.some(isPendingScan)),
    () => {
      if (!channelId) return;
      api
        .get<{ messages: Message[] }>(`/channels/${channelId}/messages?limit=50`)
        .then((res) => {
          const fresh = new Map(res.messages.map((m) => [m.id, m.attachments]));
          setMessages((prev) =>
            prev.map((m) => {
              const next = fresh.get(m.id);
              return next && m.attachments?.some(isPendingScan) ? { ...m, attachments: next } : m;
            }),
          );
        })
        .catch(() => undefined);
    },
  );

  /* ── load the first page whenever the selected channel changes ── */
  useEffect(() => {
    if (!channelId) {
      setMessages([]);
      setHasMoreOlder(false);
      return;
    }

    // Belongs to whichever channel is now open — a stale value from the
    // previous one would let the scroll handler fetch against the wrong
    // thread for the instant before the real page arrives below.
    setHasMoreOlder(false);

    let cancelled = false;
    setLoading(true);

    api
      .get<MessagePage>(`/channels/${channelId}/messages`)
      .then((res) => {
        if (cancelled) return;
        setMessages(res.messages);
        setHasMoreOlder(res.hasMore);
        setLoadedChannelId(channelId);
        // Replaced wholesale rather than merged: these belong to the channel
        // being opened, and anything held from the last one is about a
        // different conversation.
        setReadPositions(new Map((res.readPositions ?? []).map((p) => [p.userId, p.lastReadMessageId])));
        const last = res.messages[res.messages.length - 1];
        if (last) onRead(channelId, last.id);
      })
      .catch((error: unknown) => {
        if (!cancelled) toast(error instanceof ApiError ? error.message : "Could not load messages.", { tone: "danger" });
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [channelId, viewer]);

  /**
   * Set just before prepending an older page, so the "scroll to newest"
   * effect below skips that one update — loadOlder restores the reader's
   * position itself, and letting both run would yank them back down to the
   * bottom the instant older history finishes loading.
   */
  const skipAutoScrollRef = useRef(false);

  /* ── keep the thread scrolled to the newest message ── */
  useEffect(() => {
    if (skipAutoScrollRef.current) {
      skipAutoScrollRef.current = false;
      return;
    }
    const node = scrollRef.current;
    if (node) node.scrollTop = node.scrollHeight;
  }, [messages, scrollRef]);

  /*
   * …and keep it there when the thread itself changes height — the phone
   * keyboard opening or closing, or a window resize — if the reader was at
   * the bottom. Without this the newest messages slip out of view behind the
   * keyboard, or a gap opens below them when it closes.
   */
  useEffect(() => {
    const node = scrollRef.current;
    if (!node || typeof ResizeObserver === "undefined") return;
    let atBottom = true;
    const onScroll = () => {
      atBottom = node.scrollHeight - node.scrollTop - node.clientHeight < 24;
    };
    const observer = new ResizeObserver(() => {
      if (atBottom) node.scrollTop = node.scrollHeight;
    });
    node.addEventListener("scroll", onScroll, { passive: true });
    observer.observe(node);
    return () => {
      node.removeEventListener("scroll", onScroll);
      observer.disconnect();
    };
  }, [scrollRef, channelId]);

  /**
   * Fetches the page just before what is loaded and prepends it, holding the
   * reader's scroll position steady — the standard "load older" pattern: grow
   * the content above the viewport without moving what is already in view.
   */
  const loadOlder = useCallback(() => {
    if (!channelId || loadingOlder || !hasMoreOlder) return;
    // The cursor must be a confirmed message of *this* channel. Right after a
    // switch, state still holds the previous channel's thread for a render
    // (and its hasMoreOlder), and a still-pending row's id is client-made —
    // either one sent as `before` comes back 400 "Invalid pagination cursor."
    const oldest = messages.find((m) => !m.pending);
    if (!oldest || oldest.channelId !== channelId) return;
    const node = scrollRef.current;
    const previousScrollHeight = node?.scrollHeight ?? 0;

    setLoadingOlder(true);
    api
      .get<{ messages: Message[]; hasMore: boolean }>(
        `/channels/${channelId}/messages?before=${oldest.id}&limit=${MESSAGE_PAGE}`,
      )
      .then((res) => {
        if (res.messages.length === 0) {
          setHasMoreOlder(false);
          return;
        }
        skipAutoScrollRef.current = true;
        setMessages((prev) => [...res.messages, ...prev]);
        setHasMoreOlder(res.hasMore);
        // Restored after the prepend has painted: hold the reader's place by
        // pushing the scroll position down by exactly how much the content
        // above it grew, so the messages they were looking at do not move.
        requestAnimationFrame(() => {
          const el = scrollRef.current;
          if (el) el.scrollTop += el.scrollHeight - previousScrollHeight;
        });
      })
      .catch((error: unknown) => {
        toast(error instanceof ApiError ? error.message : "Could not load older messages.", { tone: "danger" });
      })
      .finally(() => setLoadingOlder(false));
  }, [channelId, loadingOlder, hasMoreOlder, messages, scrollRef, toast]);

  /**
   * Merges incoming messages into the open thread.
   *
   * Two duplicate sources have to be handled. The sender receives their own
   * message back over the socket, which must reconcile with the optimistic row
   * rather than appearing twice — matched on clientMessageId, which the
   * optimistic row uses as its temporary id. And the refetch after a
   * reconnect can overlap with what is already loaded, so a real id already
   * present wins.
   */
  const mergeIncoming = useCallback((incoming: Message[]) => {
    if (incoming.length === 0) return;
    setMessages((prev) => {
      const next = prev.slice();
      for (const message of incoming) {
        const existingIndex = next.findIndex((m) => m.id === message.id);
        if (existingIndex >= 0) {
          // Saving is personal, and a broadcast is hydrated for its sender, so
          // the copy already held knows the viewer's own state better.
          next[existingIndex] = { ...message, isSaved: next[existingIndex]!.isSaved };
          continue;
        }
        // The optimistic row is keyed by clientMessageId (its temporary id);
        // the server echo carries the real id but the same clientMessageId,
        // so match on that rather than author+body — two identical messages
        // sent back to back would otherwise match whichever pending row
        // happened to come first.
        const pendingIndex = next.findIndex(
          (m) => m.pending && message.clientMessageId && m.id === message.clientMessageId,
        );
        if (pendingIndex >= 0) {
          next[pendingIndex] = { ...message };
          continue;
        }
        next.push(message);
      }
      // A confirmed message's id is UUIDv7, so lexicographic order is
      // chronological order — but a still-pending row's id is a UUIDv4
      // generated client-side (newClientMessageId), which sorts nowhere
      // meaningful against those. Compare by createdAt instead whenever
      // either side is still pending, and fall back to id once both are
      // real so two same-millisecond messages keep a stable order.
      next.sort((a, b) => {
        if (a.pending || b.pending) return a.createdAt < b.createdAt ? -1 : a.createdAt > b.createdAt ? 1 : 0;
        return a.id < b.id ? -1 : a.id > b.id ? 1 : 0;
      });
      return next;
    });
  }, []);

  /** Sends retried across a short drop; see send-queue.ts. */
  const [sendQueue] = useState(() => new SendQueue());
  useEffect(() => () => sendQueue.dispose(), [sendQueue]);

  /** The open channel, for a reconnect refetch to check it still is on landing. */
  const channelIdRef = useRef(channelId);
  useEffect(() => {
    channelIdRef.current = channelId;
  }, [channelId]);

  /*
   * Back after a drop. Nothing is replayed over the socket, so the open
   * thread's newest page is fetched (useChannels refreshes the conversation
   * list). Waiting sends go now rather than at the end of their backoff.
   */
  useReconnected(() => {
    sendQueue.flush();
    if (!channelId) return;
    const reconnectedChannelId = channelId;
    api
      .get<MessagePage>(`/channels/${reconnectedChannelId}/messages`)
      .then((res) => {
        if (reconnectedChannelId !== channelIdRef.current) return;
        setMessages((prev) => {
          const confirmed = prev.filter((m) => !m.pending && !m.failed);
          const newestHeld = confirmed[confirmed.length - 1];
          const oldestFetched = res.messages[0];
          // More was missed than one page holds: merging would leave a hole
          // between what was on screen and the page, so start from the page
          // (keeping any of this client's own unsent rows).
          const gap = res.hasMore && newestHeld && oldestFetched && oldestFetched.id > newestHeld.id;
          return gap ? [...res.messages, ...prev.filter((m) => m.pending || m.failed)] : prev;
        });
        mergeIncoming(res.messages);
        setReadPositions(new Map((res.readPositions ?? []).map((p) => [p.userId, p.lastReadMessageId])));
        const last = res.messages[res.messages.length - 1];
        if (last) onRead(reconnectedChannelId, last.id);
      })
      .catch(() => undefined);
  });

  useRealtimeFrames((frame) => {
    switch (frame.t) {
      case "message": {
        // Another channel's message is the sidebar's business (useChannels).
        if (frame.message.channelId !== channelId) return;
        // A reply that lives in a thread is the thread panel's, not the
        // stream's; the root's reply count arrives as a `thread` frame.
        if (frame.message.inThread && !frame.message.alsoInChannel) return;
        mergeIncoming([frame.message]);
        onActivity(channelId, frame.message.createdAt);
        // Reading the channel that is already open keeps the badge at zero —
        // once somebody is actually looking at it.
        if (frame.message.authorId !== viewer?.id) onRead(channelId, frame.message.id);
        return;
      }

      case "reaction": {
        if (frame.channelId !== channelId) return;
        setMessages((prev) =>
          prev.map((m) =>
            m.id === frame.messageId
              ? {
                  ...m,
                  // `reacted` is computed here rather than sent per-recipient:
                  // the broadcast carries raw user ids for exactly this reason.
                  reactions: frame.reactions.map((r) => ({
                    emoji: r.emoji,
                    count: r.count,
                    reacted: viewer ? r.userIds.includes(viewer.id) : false,
                  })),
                }
              : m,
          ),
        );
        return;
      }

      case "thread": {
        if (frame.channelId !== channelId) return;
        setMessages((prev) =>
          prev.map((m) => (m.id === frame.rootMessageId ? { ...m, thread: frame.thread.replyCount > 0 ? frame.thread : null } : m)),
        );
        return;
      }

      case "pin": {
        if (frame.channelId !== channelId) return;
        setMessages((prev) => prev.map((m) => (m.id === frame.messageId ? { ...m, pinned: frame.pinned } : m)));
        return;
      }

      case "link_preview": {
        if (frame.channelId !== channelId) return;
        setMessages((prev) => prev.map((m) => (m.id === frame.messageId ? { ...m, linkPreview: frame.linkPreview } : m)));
        return;
      }

      case "read": {
        // Someone else reported how far they have read. Their own reading says
        // nothing about whether *they* saw your messages, so the viewer's own
        // frame is ignored rather than folded into the watermark.
        if (frame.channelId !== channelId || frame.userId === viewer?.id) return;
        setReadPositions((prev) => new Map(prev).set(frame.userId, frame.lastReadMessageId));
        return;
      }

      default:
        return;
    }
  });

  /**
   * The actual POST, shared by a fresh send and a retry of a failed one.
   *
   * `clientMessageId` is the same id the optimistic row already carries — the
   * server's idempotency key is (channelId, clientMessageId), so retrying
   * with it is safe even in the case the first attempt actually landed and
   * only the response was lost: the server returns the original message
   * instead of creating a second one.
   */
  const submitMessage = useCallback(
    (clientMessageId: string, body: string, parentMessageId: string | null) => {
      if (!channelId) return;
      const sendChannelId = channelId;

      // Queued rather than fired once: a send during a short drop is retried
      // (and again the moment the socket is back) before it is marked failed.
      sendQueue.enqueue(
        clientMessageId,
        () =>
          api.post<{ message: Message }>(`/channels/${sendChannelId}/messages`, {
            body,
            clientMessageId,
            ...(parentMessageId ? { parentMessageId } : {}),
          }),
        {
          onSent: (res) => {
            setMessages((prev) => prev.map((m) => (m.id === clientMessageId ? { ...res.message } : m)));
            onActivity(sendChannelId, res.message.createdAt);
          },
          onFailed: (error) => {
            setMessages((prev) =>
              prev.map((m) => (m.id === clientMessageId ? { ...m, pending: false, failed: true } : m)),
            );
            toast(error instanceof ApiError ? error.message : "Message failed to send.", { tone: "danger" });
          },
        },
      );
    },
    [channelId, toast, sendQueue, onActivity],
  );

  const send = useCallback(
    (body: string, parent: PendingMessage | null) => {
      if (!channelId || !viewer) return;

      const clientMessageId = newClientMessageId();

      const optimistic: PendingMessage = {
        id: clientMessageId,
        channelId,
        authorId: viewer.id,
        authorName: viewer.displayName,
        parentMessageId: parent?.id ?? null,
        // Quoted straight away rather than waiting for the server's copy, so a
        // reply does not visibly reattach itself a moment after sending.
        parent: parent ? { id: parent.id, authorName: parent.authorName, body: parent.body } : null,
        body,
        editedAt: null,
        deletedAt: null,
        // A typed message never carries a file: sharing one goes through
        // useFileUploads, which posts its own message after the upload succeeds.
        attachmentCount: 0,
        attachments: [],
        createdAt: new Date().toISOString(),
        reactions: [],
        linkedTask: null,
        pending: true,
      };
      setMessages((prev) => [...prev, optimistic]);

      submitMessage(clientMessageId, body, parent?.id ?? null);
    },
    [channelId, viewer, submitMessage],
  );

  /** Resends a message that failed — the row already exists, so this only
   *  flips it back to pending and resubmits, rather than rebuilding it. */
  const retry = useCallback(
    (message: PendingMessage) => {
      setMessages((prev) =>
        prev.map((m) => (m.id === message.id ? { ...m, pending: true, failed: false } : m)),
      );
      submitMessage(message.id, message.body, message.parentMessageId);
    },
    [submitMessage],
  );

  const replaceMessage = useCallback((message: Message) => {
    setMessages((prev) => prev.map((m) => (m.id === message.id ? { ...message } : m)));
  }, []);

  /**
   * Withdraws a message.
   *
   * Not optimistic: unlike a reaction, this is irreversible, so the row is left
   * alone until the server confirms. Showing it gone and then restoring it on
   * failure would be a worse moment than a short wait.
   */
  const deleteMessage = useCallback(
    async (messageId: string) => {
      try {
        const { message } = await api.delete<{ message: Message }>(`/messages/${messageId}`);
        replaceMessage(message);
        return true;
      } catch (error: unknown) {
        toast(error instanceof ApiError ? error.message : "Could not delete that message.", { tone: "danger" });
        return false;
      }
    },
    [replaceMessage, toast],
  );

  /** Pins or unpins for the whole channel; the `pin` frame reaches everyone else. */
  const togglePin = useCallback(
    (messageId: string, pinned: boolean) => {
      setMessages((prev) => prev.map((m) => (m.id === messageId && m.channelId === channelId ? { ...m, pinned } : m)));
      if (!channelId) return;
      const request = pinned
        ? api.put(`/channels/${channelId}/messages/${messageId}/pin`)
        : api.delete(`/channels/${channelId}/messages/${messageId}/pin`);
      request.catch((error: unknown) => {
        setMessages((prev) => prev.map((m) => (m.id === messageId ? { ...m, pinned: !pinned } : m)));
        toast(error instanceof ApiError ? error.message : "Could not update that pin.", { tone: "danger" });
      });
    },
    [channelId, toast],
  );

  /** Saves a message for the viewer alone, or takes it off their list. */
  const toggleSaved = useCallback(
    (messageId: string, saved: boolean) => {
      setMessages((prev) => prev.map((m) => (m.id === messageId ? { ...m, isSaved: saved } : m)));
      const request = saved ? api.put(`/messages/${messageId}/saved`) : api.delete(`/messages/${messageId}/saved`);
      request.catch((error: unknown) => {
        setMessages((prev) => prev.map((m) => (m.id === messageId ? { ...m, isSaved: !saved } : m)));
        toast(error instanceof ApiError ? error.message : "Could not update your saved items.", { tone: "danger" });
      });
    },
    [toast],
  );

  /** Optimistically hides the card; the `link_preview` frame confirms it for everyone. */
  const removePreview = useCallback(
    (messageId: string) => {
      let previous: Message["linkPreview"] = null;
      setMessages((prev) =>
        prev.map((m) => {
          if (m.id !== messageId) return m;
          previous = m.linkPreview;
          return { ...m, linkPreview: null };
        }),
      );
      api.delete(`/messages/${messageId}/link-preview`).catch((error: unknown) => {
        setMessages((prev) => prev.map((m) => (m.id === messageId ? { ...m, linkPreview: previous } : m)));
        toast(error instanceof ApiError ? error.message : "Could not remove that preview.", { tone: "danger" });
      });
    },
    [toast],
  );

  const attachTask = useCallback((messageId: string, task: NonNullable<Message["linkedTask"]>) => {
    setMessages((prev) => prev.map((m) => (m.id === messageId ? { ...m, linkedTask: task } : m)));
  }, []);

  const reloadLatest = useCallback(async (fromChannelId: string) => {
    const fresh = await api
      .get<{ messages: Message[] }>(`/channels/${fromChannelId}/messages?limit=50`)
      .catch(() => null);
    if (fresh) setMessages(fresh.messages);
  }, []);

  const toggleReaction = useCallback(
    (messageId: string, emoji: string) => {
      if (!viewer) return;

      // Optimistic toggle, reconciled with the server's grouped counts once the request resolves.
      setMessages((prev) =>
        prev.map((m) => {
          if (m.id !== messageId) return m;
          const existing = m.reactions?.find((r) => r.emoji === emoji);
          const nextReactions = existing
            ? m.reactions!
                .map((r) => (r.emoji === emoji ? { ...r, count: r.count - 1, reacted: false } : r))
                .filter((r) => r.count > 0)
            : [...(m.reactions ?? []), { emoji, count: 1, reacted: true }];
          return { ...m, reactions: nextReactions };
        }),
      );

      api
        .post<{ reactions: NonNullable<Message["reactions"]> }>(`/messages/${messageId}/reactions`, { emoji })
        .then((res) => {
          setMessages((prev) => prev.map((m) => (m.id === messageId ? { ...m, reactions: res.reactions } : m)));
        })
        .catch((error: unknown) => {
          toast(error instanceof ApiError ? error.message : "Could not update reaction.", { tone: "danger" });
        });
    },
    [viewer, toast],
  );

  /**
   * Set or clear the viewer's own flag on a message.
   *
   * `kind: null` clears it — a DELETE, not a PUT with a fourth value — so
   * "not flagged" keeps its one representation all the way through the UI,
   * matching the server's own rule for the same reason.
   */
  const setFlag = useCallback(
    (messageId: string, kind: MessageFlagKind | null) => {
      const previous = messages.find((m) => m.id === messageId)?.myFlag ?? null;
      setMessages((prev) => prev.map((m) => (m.id === messageId ? { ...m, myFlag: kind } : m)));

      const request = kind
        ? api.put<{ message: Message }>(`/messages/${messageId}/flag`, { kind })
        : api.delete<{ message: Message }>(`/messages/${messageId}/flag`);

      request
        .then((res) => {
          setMessages((prev) => prev.map((m) => (m.id === messageId ? { ...m, myFlag: res.message.myFlag ?? null } : m)));
        })
        .catch((error: unknown) => {
          setMessages((prev) => prev.map((m) => (m.id === messageId ? { ...m, myFlag: previous } : m)));
          toast(error instanceof ApiError ? error.message : "Could not update that flag.", { tone: "danger" });
        });
    },
    [messages, toast],
  );

  const remind = useCallback(
    (messageId: string, at: Date) => {
      api
        .post<{ reminder: { id: string } }>(`/messages/${messageId}/reminders`, { remindAt: at.toISOString() })
        .then(() => {
          const when = at.toLocaleString(undefined, {
            weekday: "short",
            hour: "numeric",
            minute: "2-digit",
          });
          toast(`Reminder set for ${when}.`);
        })
        .catch((error: unknown) => {
          toast(error instanceof ApiError ? error.message : "Could not set that reminder.", { tone: "danger" });
        });
    },
    [toast],
  );

  return {
    messages,
    loading,
    loadedChannelId,
    hasMoreOlder,
    loadingOlder,
    readWatermark,
    loadOlder,
    send,
    retry,
    toggleReaction,
    setFlag,
    remind,
    deleteMessage,
    replaceMessage,
    attachTask,
    togglePin,
    toggleSaved,
    removePreview,
    reloadLatest,
  };
}
